/**
 * FinanceApp - Copyright (c) 2026 Emmanuel TESTE
 * Tous droits réservés - Logiciel propriétaire
 */
import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { KpiCard } from "../components/ui/Card";
import GrowthDiscoveryCard from "../components/GrowthDiscoveryCard";
import OnboardingChecklist from "../components/OnboardingChecklist";
import api from "../services/api";
import {
  TrendingUp, FileText, Landmark, ShieldCheck,
  RefreshCw, BarChart2, Users, AlertTriangle,
  CheckCircle, Package, Mail, CreditCard,
  CalendarDays, ArrowRight, Plus, TrendingDown, Edit3, Bot,
} from "lucide-react";

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("fr-FR"); }

const INV_STATUS = {
  paid:      { label: "Payée",      color: "var(--success)", bg: "rgba(16,185,129,0.12)" },
  pending:   { label: "En attente", color: "var(--warning)", bg: "rgba(245,158,11,0.12)" },
  late:      { label: "En retard",  color: "var(--danger)",  bg: "rgba(239,68,68,0.12)"  },
  draft:     { label: "Brouillon",  color: "var(--text-muted)", bg: "rgba(107,114,128,0.12)" },
  cancelled: { label: "Annulée",    color: "var(--text-muted)", bg: "rgba(107,114,128,0.12)" },
};

const cardStyle = {
  background: "var(--bg-card)",
  border: "var(--border)",
  borderRadius: 12,
  padding: 18,
  boxShadow: "var(--shadow)",
};

const CardHeader = ({ title, icon: Icon, onSeeAll }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
      {Icon && <Icon size={16} color="var(--accent)" />}
      {title}
    </div>
    {onSeeAll && (
      <button
        onClick={onSeeAll}
        style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12, color: "var(--accent)", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 500 }}
      >
        Voir <ArrowRight size={12} />
      </button>
    )}
  </div>
);

export default function Dashboard({ onNavigate, currentCompanyId }) {
  const [kpis, setKpis]               = useState(null);
  const [chart, setChart]             = useState([]);
  const [urgent, setUrgent]           = useState([]);
  const [relances, setRelances]       = useState([]);
  const [subStats, setSubStats]       = useState(null);
  const [subDueSoon, setSubDueSoon]   = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [aiUsage, setAiUsage]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [yoy, setYoy]                 = useState(null);
  const [showManualKpi, setShowManualKpi] = useState(false);
  const [manualForm, setManualForm]   = useState({ year: new Date().getFullYear() - 1, revenue_ht: "", charges_ht: "", notes: "" });
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    const cid = currentCompanyId;
    Promise.all([
      api.dashboard.kpis(cid),
      api.dashboard.chart(cid),
      api.dashboard.urgent(cid),
      api.relances.list({ status: "pending" }),
      api.subscriptions.stats().catch(() => null),
      api.subscriptions.dueSoon(7).catch(() => []),
      api.stocks.productAlerts().catch(() => []),
      api.invoices.list({ limit: 5 }).catch(() => ({ invoices: [] })),
      api.accounting.health().catch(() => null),
      api.dashboard.yoyComparison(cid).catch(() => null),
      // .catch(() => null) : /ai/usage nécessite le module "analytique"
      // (server/routes/ai.js, requireModule sur tout le routeur) — pas
      // systématiquement actif selon la société, ne doit jamais faire
      // échouer le reste du tableau de bord.
      api.ai.usage().catch(() => null),
    ]).then(([k, c, u, r, ss, sds, sa, inv, hs, yc, au]) => {
      setKpis(k);
      setChart(c);
      setUrgent(u.slice(0, 3));
      setRelances(r.slice(0, 3));
      setSubStats(ss);
      setSubDueSoon(Array.isArray(sds) ? sds.slice(0, 5) : []);
      setStockAlerts(Array.isArray(sa) ? sa.slice(0, 5) : []);
      setRecentInvoices((inv?.invoices || []).slice(0, 5));
      setHealthScore(hs);
      setYoy(yc);
      setAiUsage(au);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  function reloadYoy() {
    if (!currentCompanyId) return;
    api.dashboard.yoyComparison(currentCompanyId).then(setYoy).catch(() => {});
  }

  async function saveManualKpi() {
    if (!manualForm.revenue_ht || !manualForm.charges_ht) return;
    setSavingManual(true);
    try {
      await api.dashboard.saveManualKpi(currentCompanyId, manualForm);
      setShowManualKpi(false);
      reloadYoy();
    } catch (err) { console.error(err); }
    finally { setSavingManual(false); }
  }

  if (loading || !kpis) return (
    <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>
      <RefreshCw size={28} style={{ opacity: 0.4, marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
      Chargement…
    </div>
  );

  const mrr = subStats?.mrr || 0;
  const arr = subStats?.arr || 0;

  return (
    <div>
      {/* En-tête page */}
      <div className="page-header">
        <h2 className="page-title">Tableau de bord</h2>
        <div className="btn-group">
          <button className="btn" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <CalendarDays size={14} />
            {new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
          </button>
          <button className="btn primary" style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={() => onNavigate("facturation")}>
            <Plus size={14} /> Nouvelle facture
          </button>
        </div>
      </div>

      <OnboardingChecklist currentCompanyId={currentCompanyId} onNavigate={onNavigate} />
      <GrowthDiscoveryCard currentCompanyId={currentCompanyId} onNavigate={onNavigate} />

      {/* Comparaison N vs N-1 */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <CardHeader title="Évolution de l'activité — exercice en cours vs précédent" icon={TrendingUp} />
        {!yoy ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Chargement…</div>
        ) : yoy.previous_year_source === null ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Aucune donnée disponible pour l'exercice précédent — importez votre FEC ou saisissez vos chiffres clés pour activer la comparaison.
            </div>
            <button className="btn" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }} onClick={() => setShowManualKpi(true)}>
              <Edit3 size={13} /> Saisir mes chiffres N-1
            </button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>
              Du {fmtDate(yoy.period.from)} à aujourd'hui, vs même période l'exercice précédent
              {yoy.previous_year_source === 'manual' && <span> · <em>données N-1 saisies manuellement</em></span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10 }}>
              <div style={{ padding: 16, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 10, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <TrendingUp size={20} color="var(--success)" />
                <div style={{ fontSize: 13, color: "var(--text-muted)", margin: "10px 0 4px" }}>Chiffre d'affaires</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: "var(--text-primary)" }}>{fmt(yoy.revenue.current)} €</div>
                <div style={{ fontSize: 12, marginTop: 8, color: yoy.revenue.growth_pct == null ? "var(--text-muted)" : yoy.revenue.growth_pct >= 0 ? "var(--success)" : "var(--danger)", display: "flex", alignItems: "center", gap: 4 }}>
                  {yoy.revenue.growth_pct != null && (yoy.revenue.growth_pct >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />)}
                  {yoy.revenue.growth_pct != null ? `${yoy.revenue.growth_pct > 0 ? '+' : ''}${yoy.revenue.growth_pct}%` : '—'}
                  <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>vs {fmt(yoy.revenue.previous)} €</span>
                </div>
                {/* Ventes (comptes 70) seulement ; autres produits d'exploitation (71 à 75) à part — 27/10/2026 */}
                {yoy.other_income && (Math.abs(yoy.other_income.current || 0) >= 0.005 || Math.abs(yoy.other_income.previous || 0) >= 0.005) && (
                  <div style={{ fontSize: 12, marginTop: 10, color: "var(--text-muted)" }}>
                    Autres produits d'exploitation : <strong style={{ color: "var(--text-primary)" }}>{fmt(yoy.other_income.current)} €</strong>
                    {yoy.other_income.previous != null && <span> (vs {fmt(yoy.other_income.previous)} €)</span>}
                  </div>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 10 }}>
                {[
                  { label: "Charges", data: yoy.charges, bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.2)", iconColor: "var(--danger)" },
                  { label: "Résultat", data: yoy.result, bg: "rgba(59,130,246,0.08)", border: "rgba(59,130,246,0.2)", iconColor: "var(--info)" },
                  {
                    label: "Marge nette",
                    data: {
                      current: yoy.revenue.current > 0 ? Math.round((yoy.result.current / yoy.revenue.current) * 1000) / 10 : null,
                      previous: yoy.revenue.previous > 0 ? Math.round((yoy.result.previous / yoy.revenue.previous) * 1000) / 10 : null,
                    },
                    isPercent: true, bg: "rgba(124,58,237,0.08)", border: "rgba(124,58,237,0.2)", iconColor: "#7c3aed",
                  },
                  {
                    label: "Taux de charges",
                    data: {
                      current: yoy.revenue.current > 0 ? Math.round((yoy.charges.current / yoy.revenue.current) * 1000) / 10 : null,
                      previous: yoy.revenue.previous > 0 ? Math.round((yoy.charges.previous / yoy.revenue.previous) * 1000) / 10 : null,
                    },
                    isPercent: true, invertColor: true, bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.2)", iconColor: "var(--warning)",
                  },
                ].map(({ label, data, isPercent, invertColor, bg, border, iconColor }) => {
                  const positive = data.growth_pct != null && data.growth_pct >= 0;
                  const GrowthIcon = positive ? TrendingUp : TrendingDown;
                  const color = isPercent
                    ? (data.current == null ? "var(--text-muted)" : (invertColor ? data.current <= 100 : data.current >= 0) ? "var(--success)" : "var(--danger)")
                    : (data.growth_pct == null ? "var(--text-muted)" : positive ? "var(--success)" : "var(--danger)");
                  return (
                    <div key={label} style={{ padding: 12, background: bg, border: `1px solid ${border}`, borderRadius: 10 }}>
                      <TrendingUp size={16} color={iconColor} style={{ opacity: 0.7 }} />
                      <div style={{ fontSize: 12, color: "var(--text-muted)", margin: "8px 0 2px" }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>
                        {data.current == null ? '—' : isPercent ? `${data.current}%` : `${fmt(data.current)} €`}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontSize: 11, color }}>
                        {isPercent
                          ? (data.previous != null ? <span style={{ color: "var(--text-muted)" }}>vs {data.previous}% l'an dernier</span> : '—')
                          : (<>
                              {data.growth_pct != null && <GrowthIcon size={12} />}
                              {data.growth_pct != null ? `${data.growth_pct > 0 ? '+' : ''}${data.growth_pct}%` : '—'}
                              <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>vs {fmt(data.previous)} €</span>
                            </>)
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal saisie manuelle KPI N-1 */}
      {showManualKpi && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ ...cardStyle, width: "100%", maxWidth: 420 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Saisir les chiffres de l'exercice précédent</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowManualKpi(false)}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
              Ces chiffres serviront uniquement de point de comparaison — ils seront automatiquement remplacés dès que de vraies écritures existeront pour cette période.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Année</label>
                <input className="input" type="number" value={manualForm.year} onChange={e => setManualForm(f => ({ ...f, year: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Chiffre d'affaires HT (€)</label>
                <input className="input" type="number" step="0.01" value={manualForm.revenue_ht} onChange={e => setManualForm(f => ({ ...f, revenue_ht: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Charges HT (€)</label>
                <input className="input" type="number" step="0.01" value={manualForm.charges_ht} onChange={e => setManualForm(f => ({ ...f, charges_ht: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes (optionnel)</label>
                <input className="input" value={manualForm.notes} onChange={e => setManualForm(f => ({ ...f, notes: e.target.value }))} placeholder="ex: chiffres issus de la liasse fiscale" />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
              <button className="btn" onClick={() => setShowManualKpi(false)}>Annuler</button>
              <button className="btn primary" disabled={savingManual} onClick={saveManualKpi}>{savingManual ? "…" : "Enregistrer"}</button>
            </div>
          </div>
        </div>
      )}


      {/* KPIs principaux — ligne 1 */}
      <div className="kpi-grid k4">
        <KpiCard
          label="CA mensuel"
          value={`${fmt(kpis.ca_month)} €`}
          sub="Factures encaissées ce mois"
          icon={<TrendingUp size={20} />}
          color="var(--accent)"
        />
        <KpiCard
          label="Factures impayées"
          value={`${fmt(kpis.unpaid_amount)} €`}
          sub={`${kpis.unpaid_count} en attente`}
          icon={<FileText size={20} />}
          color="var(--warning)"
        />
        <KpiCard
          label="Solde bancaire"
          value={`${fmt(kpis.bank_balance)} €`}
          sub={`${kpis.bank_accounts} compte${kpis.bank_accounts > 1 ? "s" : ""}`}
          icon={<Landmark size={20} />}
          color="var(--info)"
        />
        <KpiCard
          label="Score sécurité"
          value={`${kpis.security_score}/100`}
          sub={kpis.security_score >= 90 ? "Excellent" : kpis.security_score >= 75 ? "Bon" : "À améliorer"}
          icon={<ShieldCheck size={20} />}
          color="var(--success)"
        />
      </div>

      {/* KPIs abonnements — ligne 2 */}
      {subStats && (
        <div className="kpi-grid k4">
          <div onClick={() => onNavigate("abonnements")} style={{ cursor: "pointer" }}>
            <KpiCard label="MRR" value={`${fmt(mrr)} €`} sub="Revenu mensuel récurrent" icon={<RefreshCw size={20} />} color="var(--success)" />
          </div>
          <div onClick={() => onNavigate("abonnements")} style={{ cursor: "pointer" }}>
            <KpiCard label="ARR" value={`${fmt(arr)} €`} sub="Revenu annuel récurrent" icon={<BarChart2 size={20} />} color="var(--accent)" />
          </div>
          <div onClick={() => onNavigate("abonnements")} style={{ cursor: "pointer" }}>
            <KpiCard label="Abonnés actifs" value={String(subStats.active_count)} sub={`${subStats.churn_rate}% churn`} icon={<Users size={20} />} color="var(--info)" />
          </div>
          <div onClick={() => onNavigate("stocks")} style={{ cursor: "pointer" }}>
            <KpiCard
              label="Alertes stocks"
              value={String(stockAlerts.length)}
              sub={stockAlerts.length > 0 ? "Réapprovisionnement requis" : "Niveaux OK"}
              icon={<AlertTriangle size={20} />}
              color={stockAlerts.length > 0 ? "var(--warning)" : "var(--success)"}
            />
          </div>
        </div>
      )}

      <div className="g2">
        {/* Graphique revenus vs dépenses */}
        <div style={cardStyle}>
          <CardHeader title="Revenus vs Dépenses" icon={BarChart2} />
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chart} barGap={2}>
              <XAxis dataKey="m" tick={{ fontSize: 11, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "var(--text-muted)" }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "var(--bg-card)", border: "var(--border)", borderRadius: 8, fontSize: 12 }}
                formatter={v => `${v.toLocaleString("fr-FR")} €`}
              />
              <Bar dataKey="ca"  fill="var(--accent)" radius={[4, 4, 0, 0]} name="CA" />
              <Bar dataKey="dep" fill="rgba(99,102,241,0.25)" radius={[4, 4, 0, 0]} name="Dépenses" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Trésorerie consolidée */}
        <div style={cardStyle}>
          <CardHeader title="Trésorerie consolidée" icon={Landmark} onSeeAll={() => onNavigate("tresorerie")} />
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 30, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.5px" }}>{fmt(kpis.bank_balance)} €</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Solde groupe · {kpis.bank_accounts} compte{kpis.bank_accounts > 1 ? "s" : ""}</div>
          </div>
          <div className="g2" style={{ marginBottom: 12, gap: 8 }}>
            <div style={{ padding: "10px 12px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Entrées (30j)</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--success)" }}>+{fmt(chart.slice(-1)[0]?.ca || 0)} €</div>
            </div>
            <div style={{ padding: "10px 12px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Sorties (30j)</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--danger)" }}>-{fmt(chart.slice(-1)[0]?.dep || 0)} €</div>
            </div>
          </div>
          {kpis.pending_expenses_count > 0 && (
            <div style={{ padding: "8px 10px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, fontSize: 12, color: "var(--warning)", display: "flex", alignItems: "center", gap: 6 }}>
              <FileText size={13} />
              {kpis.pending_expenses_count} note(s) de frais à valider · {fmt(kpis.pending_expenses)} €
            </div>
          )}
        </div>
      </div>

      <div className="g2">
        {/* Dernières factures */}
        <div style={cardStyle}>
          <CardHeader title="Dernières factures" icon={FileText} onSeeAll={() => onNavigate("facturation")} />
          {recentInvoices.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 0" }}>Aucune facture</div>
          ) : recentInvoices.map(inv => {
            const sc = INV_STATUS[inv.status] || INV_STATUS.draft;
            return (
              <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "var(--border)" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.client_name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>{inv.number} · {fmtDate(inv.date)}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{fmt(inv.ttc_amount)} €</div>
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 600, background: sc.bg, color: sc.color, display: "inline-block", marginTop: 2 }}>{sc.label}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Paiements urgents */}
        <div style={cardStyle}>
          <CardHeader title="Paiements urgents" icon={CreditCard} onSeeAll={() => onNavigate("paiements")} />
          {urgent.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 0", display: "flex", alignItems: "center", gap: 6 }}>
              <CheckCircle size={14} color="var(--success)" /> Aucun paiement urgent
            </div>
          ) : urgent.map((inv, i) => {
            const overdue = Math.round(inv.days_overdue || 0);
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: overdue > 0 ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)", border: `1px solid ${overdue > 0 ? "rgba(239,68,68,0.2)" : "rgba(245,158,11,0.2)"}`, borderRadius: 8, marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{inv.client_name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                    {overdue > 0 ? `${overdue}j de retard` : `Éch. ${fmtDate(inv.due_date)}`}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{fmt(inv.ttc_amount)} €</div>
                  <button className="btn primary sm" style={{ marginTop: 4, fontSize: 11 }} onClick={() => onNavigate("facturation")}>Voir</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="g2">
        {/* Abonnements expirant cette semaine */}
        <div style={cardStyle}>
          <CardHeader title="Abonnements — échéances semaine" icon={RefreshCw} onSeeAll={() => onNavigate("abonnements")} />
          {subDueSoon.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 0", display: "flex", alignItems: "center", gap: 6 }}>
              <CheckCircle size={14} color="var(--success)" /> Aucune échéance cette semaine
            </div>
          ) : subDueSoon.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "var(--border)" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--success)", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.customer_name}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.name}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--warning)" }}>{fmtDate(s.next_billing_date)}</div>
                <div style={{ fontSize: 11, color: "var(--success)", marginTop: 1 }}>{fmt(s.amount_ttc)} € TTC</div>
              </div>
            </div>
          ))}
          {subStats && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "var(--border)", display: "flex", gap: 16, fontSize: 12 }}>
              <span style={{ color: "var(--success)" }}>MRR : <strong>{fmt(mrr)} €</strong></span>
              <span style={{ color: "var(--text-muted)" }}>ARR : <strong>{fmt(arr)} €</strong></span>
              {subStats.expiring_soon > 0 && <span style={{ color: "var(--warning)" }}><strong>{subStats.expiring_soon}</strong> expiration(s) 30j</span>}
            </div>
          )}
        </div>

        {/* Alertes stocks + relances */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Alertes stocks */}
          <div style={cardStyle}>
            <CardHeader title="Alertes stocks" icon={Package} onSeeAll={() => onNavigate("stocks")} />
            {stockAlerts.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "4px 0", display: "flex", alignItems: "center", gap: 6 }}>
                <CheckCircle size={14} color="var(--success)" /> Tous les niveaux de stock sont OK
              </div>
            ) : stockAlerts.map(p => {
              const isOut = p.stock_current <= 0;
              return (
                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "var(--border)" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: isOut ? "var(--danger)" : "var(--warning)", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.sku}</div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 12, flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color: isOut ? "var(--danger)" : "var(--warning)" }}>{p.stock_current} {p.unit}</div>
                    <div style={{ color: "var(--text-muted)" }}>min {p.stock_min}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Santé comptable */}
          {healthScore !== null && (
            <div style={{ ...cardStyle, borderTop: `3px solid ${(healthScore?.score ?? 0) >= 90 ? "var(--success)" : (healthScore?.score ?? 0) >= 70 ? "var(--warning)" : "var(--danger)"}` }}>
              <CardHeader title="Santé comptable" icon={ShieldCheck} onSeeAll={() => onNavigate("comptabilite")} />
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 36, fontWeight: 900, color: (healthScore?.score ?? 0) >= 90 ? "var(--success)" : (healthScore?.score ?? 0) >= 70 ? "var(--warning)" : "var(--danger)", lineHeight: 1 }}>{healthScore?.score ?? "—"}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>/ 100</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{healthScore?.check_date ? `Au ${fmtDate(healthScore.check_date)}` : ""}</div>
                </div>
              </div>
              {(healthScore?.issues || []).length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
                  <CheckCircle size={14} color="var(--success)" /> Tous les contrôles sont OK
                </div>
              ) : (healthScore?.issues || []).slice(0, 3).map((issue, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", borderBottom: "var(--border)", fontSize: 12 }}>
                  <AlertTriangle size={13} color="var(--danger)" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ color: "var(--text-primary)" }}>{issue.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Moteur IA — reprend le bandeau + la métrique d'AISettings.jsx,
              retirée de la navigation (contenu résiduel déplacé ici) */}
          {aiUsage !== null && (
            <div style={cardStyle}>
              <CardHeader title="Moteur IA" icon={Bot} />
              <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5, marginBottom: 12 }}>
                Mistral AI (société française), inclus dans votre forfait — aucune clé à fournir.
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: "var(--accent)", lineHeight: 1 }}>{aiUsage.total_conversations || 0}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>conversation{(aiUsage.total_conversations || 0) > 1 ? "s" : ""} avec l'assistant IA</div>
              </div>
            </div>
          )}

          {/* Prochaines relances */}
          <div style={cardStyle}>
            <CardHeader title="Prochaines relances" icon={Mail} onSeeAll={() => onNavigate("relances")} />
            {relances.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "4px 0", display: "flex", alignItems: "center", gap: 6 }}>
                <CheckCircle size={14} color="var(--success)" /> Aucune relance en attente
              </div>
            ) : relances.map((r, i) => {
              const isCritical = r.days_overdue > 15;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: i < relances.length - 1 ? "var(--border)" : "none" }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>{r.client_name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                      {r.days_overdue > 0 ? `${r.days_overdue}j retard` : "Préventive"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>{fmt(r.amount)} €</div>
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 600, background: isCritical ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.12)", color: isCritical ? "var(--danger)" : "var(--warning)", display: "inline-block", marginTop: 2 }}>
                      {isCritical ? "Critique" : "En retard"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
